import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TooltipProps } from "./types";
import { classNames } from "helpers";

const GAP = 8;

const Tooltip: React.FC<TooltipProps> = ({
  icon = "!",
  variant,
  size = "small",
  description,
  children,
  position = "top-right",
  trigger,
  decorate,
  open = false,
  interactive = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  // Kontrolirano otvaranje (npr. odabrani pin) uz hover — ne isključuju se međusobno.
  const isOpen = open || isHovered;
  const triggerRef = useRef<HTMLSpanElement>(null);
  // Prelazak s okidača na popup ide preko praznine, pa se zatvaranje odgađa.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const handleOpen = useCallback(() => {
    cancelClose();
    setIsHovered(true);
  }, [cancelClose]);

  const handleClose = useCallback(() => {
    if (!interactive) {
      setIsHovered(false);
      return;
    }

    cancelClose();
    closeTimer.current = setTimeout(() => setIsHovered(false), 150);
  }, [interactive, cancelClose]);

  useEffect(() => cancelClose, [cancelClose]);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const variantClassName = () => {
    switch (variant) {
      case "info":
        return "bg-sky-800 text-gray-50";
      case "warning":
        return "bg-yellow-500 text-yellow-50";
      case "danger":
        return "bg-red-500 text-gray-50";
      case "dark":
        return "bg-slate-800 text-white";
      default:
        return "bg-red-500 text-gray-50";
    }
  };

  const sizeClassName = () => {
    switch (size) {
      case "small":
        return "text-xs min-w-max";
      case "medium":
        return "text-sm min-w-[200px] max-w-[300px] whitespace-normal";
      case "large":
        return "text-sm min-w-[300px] max-w-[400px] whitespace-normal";
      default:
        return "text-xs min-w-max";
    }
  };

  const transformClassName = () => {
    switch (position) {
      case "top-right":
        return "-translate-y-full";
      case "top-left":
        return "-translate-y-full -translate-x-full";
      case "top-center":
        return "-translate-y-full -translate-x-1/2";
      case "bottom-right":
        return "";
      case "bottom-left":
        return "-translate-x-full";
      default:
        return "-translate-y-full";
    }
  };

  const updateCoords = useCallback(() => {
    const node = triggerRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    let top = 0;
    let left = 0;
    switch (position) {
      case "top-right":
        top = rect.top - GAP;
        left = rect.right;
        break;
      case "top-left":
        top = rect.top - GAP;
        left = rect.left;
        break;
      case "top-center":
        top = rect.top - GAP;
        left = rect.left + rect.width / 2;
        break;
      case "bottom-right":
        top = rect.bottom + GAP;
        left = rect.left;
        break;
      case "bottom-left":
        top = rect.bottom + GAP;
        left = rect.right;
        break;
    }
    setCoords({ top, left });
  }, [position]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updateCoords();
  }, [isOpen, updateCoords]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = () => updateCoords();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [isOpen, updateCoords]);

  const hasCustomContent = !!children;

  const popupClasses = classNames(
    hasCustomContent
      ? "bg-slate-800 text-white rounded-lg shadow-xl p-4"
      : classNames(variantClassName(), sizeClassName(), "px-4 pt-3 pb-3 rounded"),
    transformClassName(),
    interactive ? "pointer-events-auto" : "pointer-events-none"
  );

  return (
    <>
      <span
        ref={triggerRef}
        className="relative flex"
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
      >
        {trigger ? (
          trigger
        ) : decorate ? (
          <span className="cursor-help border-b border-dashed border-slate-400 leading-tight">
            {decorate}
          </span>
        ) : (
          <span
            className={classNames(
              hasCustomContent ? "bg-slate-200 text-slate-500" : variantClassName(),
              "w-5 h-5 rounded-full inline-flex items-center justify-center text-xs font-bold text-center cursor-help"
            )}
          >
            {icon}
          </span>
        )}
      </span>
      {isOpen && coords && typeof document !== "undefined"
        ? createPortal(
            <div
              className={popupClasses}
              onMouseEnter={interactive ? handleOpen : undefined}
              onMouseLeave={interactive ? handleClose : undefined}
              style={{
                position: "fixed",
                top: coords.top,
                left: coords.left,
                zIndex: 9999,
              }}
            >
              {hasCustomContent ? children : description}
            </div>,
            document.body
          )
        : null}
    </>
  );
};

export default React.memo(Tooltip);
